import React, { useState, useEffect } from 'react';
import { Node } from 'reactflow';
import { ShieldCheck, Code, Send, Mail, Database, Loader, Zap } from 'lucide-react';
import type { NodeData } from '../CommonNode/CommonNode';

type ControlModalProps = {
  node: Node<NodeData>;
  onSave: (label: string, type: string, settings: Record<string, any>) => void;
  onDelete: () => void;
  onClose: () => void;
};

const actionTypes = [
  'Start',
  'Stop',
  'Message',
  'If/Else',
  'Set Variable',
  'Webhook',
  'Email Trigger',
  'API Call',
  'LLM Call',
  'Database Query',
  'Slack Message',
  'Delay'
];

const ControlModal: React.FC<ControlModalProps> = ({
  node,
  onSave,
  onDelete,
  onClose
}) => {
  const [label, setLabel] = useState(node.data.label || '');
  const [type, setType] = useState(node.data.type || '');
  const [settings, setSettings] = useState<Record<string, any>>(node.data.settings || {});
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    if (type && !label) setLabel(type);
  }, [type]);

  const handleSettingChange = (key: string, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const validateAndSetJSON = (key: string, value: string) => {
    try {
      const parsed = JSON.parse(value);
      setJsonError('');
      handleSettingChange(key, parsed);
    } catch (e) {
      setJsonError('Invalid JSON format');
    }
  };

  const renderSettings = () => {
    const commonRoleSetting = (
      <Setting label="Required Role">
        <Input
          value={settings.role || ''}
          onChange={(v) => handleSettingChange('role', v)}
          placeholder="admin, manager, user..."
        />
      </Setting>
    );

    switch (type) {
      case 'Webhook':
        return (
          <Grid>
            {commonRoleSetting}
            <Setting label="Webhook URL">
              <Input value={settings.url || ''} onChange={v => handleSettingChange('url', v)} />
            </Setting>
            <Setting label="Method">
              <Select value={settings.method || 'POST'} options={['GET', 'POST']} onChange={v => handleSettingChange('method', v)} />
            </Setting>
            <Setting label="Query Parameters (JSON)">
              <Textarea value={JSON.stringify(settings.query || {}, null, 2)} onChange={v => validateAndSetJSON('query', v)} />
            </Setting>
            <Setting label="Request Body (JSON)">
              <Textarea value={JSON.stringify(settings.body || {}, null, 2)} onChange={v => validateAndSetJSON('body', v)} />
            </Setting>
          </Grid>
        );
      case 'LLM Call':
        return (
          <Grid>
            {commonRoleSetting}
            <Setting label="Model">
              <Select
                value={settings.model || ''}
                options={['ChatGPT-4', 'Claude 3', 'Gemini']}
                onChange={(v) => handleSettingChange('model', v)}
              />
            </Setting>
            <Setting label="Prompt">
              <Textarea value={settings.prompt || ''} onChange={(v) => handleSettingChange('prompt', v)} />
            </Setting>
            <Setting label="Memory Scope">
              <Select
                value={settings.scope || ''}
                options={['Global', 'Project', 'Session']}
                onChange={(v) => handleSettingChange('scope', v)}
              />
            </Setting>
            <Setting label="Variables (JSON)">
              <Textarea
                value={JSON.stringify(settings.variables || {}, null, 2)}
                onChange={(v) => validateAndSetJSON('variables', v)}
              />
              {jsonError && <ErrorText message={jsonError} />}
            </Setting>
          </Grid>
        );

      case 'API Call':
        return (
          <Grid>
            {commonRoleSetting}
            <Setting label="Endpoint URL">
              <Input value={settings.url || ''} onChange={(v) => handleSettingChange('url', v)} />
            </Setting>
            <Setting label="Method">
              <Select
                value={settings.method || 'POST'}
                options={['GET', 'POST', 'PUT', 'DELETE']}
                onChange={(v) => handleSettingChange('method', v)}
              />
            </Setting>
            <Setting label="Query Params (key=value JSON)">
              <Textarea
                value={JSON.stringify(settings.query || {}, null, 2)}
                onChange={(v) => validateAndSetJSON('query', v)}
              />
            </Setting>
            <Setting label="Request Body (JSON)">
              <Textarea
                value={JSON.stringify(settings.body || {}, null, 2)}
                onChange={(v) => validateAndSetJSON('body', v)}
              />
            </Setting>
            <Setting label="Headers (auto fetches from vault if left blank)">
              <Textarea
                value={JSON.stringify(settings.headers || {}, null, 2)}
                onChange={(v) => validateAndSetJSON('headers', v)}
              />
            </Setting>
          </Grid>
        );

      case 'Email Trigger':
        return (
          <Grid>
            {commonRoleSetting}
            <Setting label="Subject Filter">
              <Input value={settings.subject || ''} onChange={v => handleSettingChange('subject', v)} />
            </Setting>
            <Setting label="Sender Filter">
              <Input value={settings.from || ''} onChange={v => handleSettingChange('from', v)} />
            </Setting>
            <Setting label="Email Template (HTML)">
              <Textarea value={settings.template || ''} onChange={v => handleSettingChange('template', v)} />
            </Setting>
          </Grid>
        );

      case 'Database Query':
        return (
          <Grid>
            {commonRoleSetting}
            <Setting label="Connection String">
              <Input value={settings.conn || ''} onChange={(v) => handleSettingChange('conn', v)} />
            </Setting>
            <Setting label="SQL Query">
              <Textarea value={settings.sql || ''} onChange={(v) => handleSettingChange('sql', v)} />
            </Setting>
          </Grid>
        );

      case 'Slack Message':
        return (
          <Grid>
            {commonRoleSetting}
            <Setting label="Slack Channel">
              <Input value={settings.channel || ''} onChange={(v) => handleSettingChange('channel', v)} />
            </Setting>
            <Setting label="Message Text">
              <Textarea value={settings.message || ''} onChange={(v) => handleSettingChange('message', v)} />
            </Setting>
          </Grid>
        );

      case 'Delay':
        return (
          <Grid>
            {commonRoleSetting}
            <Setting label="Delay Duration (ms)">
              <Input
                type="number"
                value={settings.duration || ''}
                onChange={(v) => handleSettingChange('duration', v)}
              />
            </Setting>
          </Grid>
        );

      default:
        return <p className="text-gray-400 mt-4">Select a node type to begin configuration.</p>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-gray-900 text-white w-full max-w-5xl rounded-lg shadow-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <ShieldCheck className="text-green-400" size={20} /> Configure Node
        </h2>
        <div className="space-y-4">
          <Setting label="Title">
            <Input value={label} onChange={setLabel} />
          </Setting>
          <Setting label="Type">
            <Select value={type} options={actionTypes} onChange={setType} />
          </Setting>
          {renderSettings()}
        </div>
        <div className="mt-6 flex justify-between">
          <button className="bg-gray-700 px-4 py-2 rounded" onClick={onClose}>Cancel</button>
          <div className="space-x-2">
            <button
              className="bg-slate-700 hover:bg-slate-600 text-white font-medium px-4 py-2 rounded-md border border-slate-500 transition duration-150"
              onClick={onDelete}
            >
              Delete
            </button>

            <button
              className="bg-slate-600 hover:bg-slate-500 text-white font-medium px-4 py-2 rounded-md border border-indigo-500 transition duration-150"
              onClick={() => onSave(label, type, settings)}
            >
              Save
            </button>

          </div>
        </div>
      </div>
    </div>
  );
};

const Setting: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm text-gray-300 mb-1 font-medium">{label}</label>
    {children}
  </div>
);

const Input: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string; type?: string }> = ({
  value,
  onChange,
  placeholder,
  type = 'text'
}) => (
  <input
    type={type}
    placeholder={placeholder}
    className="w-full p-2 bg-gray-800 rounded text-white"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

const Textarea: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <textarea
    className="w-full p-2 bg-gray-800 rounded text-white min-h-[80px]"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

const Select: React.FC<{ value: string; options: string[]; onChange: (v: string) => void }> = ({
  value,
  options,
  onChange
}) => (
  <select className="w-full p-2 bg-gray-800 rounded text-white" value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">-- Select --</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
);

const ErrorText: React.FC<{ message: string }> = ({ message }) => (
  <p className="text-sm text-red-400 mt-1">{message}</p>
);

export default ControlModal;
